import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Clear as ClearIcon,
  AutoStories as StoriesIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
  AdminPanelSettings as AdminIcon,
  FilterList as FilterListIcon,
  SwapVert as SortIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { solutionStoriesApi, SolutionStory, SolutionStoryFilters } from '../services/api';
import { SolutionStoryDetailModal } from '../components/SolutionStoryDetailModal';
import { StoryCard } from '../components/solution-stories/StoryCard';
import {
  dedupeIndustryFilterOptions,
  expandIndustryToCanonical,
  industryFilterValuesForApi,
} from '../components/solution-stories/storyHelpers';
import {
  EXAMPLE_QUERIES,
  STORY_UI,
} from '../components/solution-stories/storyUiTokens';
import { SOLUTION_STORY_OPTIONS_QUERY_KEY } from '../components/solution-stories/useSolutionStoryOptions';

// ─────────────────────────────────────────────────
// Multi-select filter dropdown (styled as HTML chip pills)
// ─────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  searchable = false,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [menuSearch, setMenuSearch] = useState('');
  const open = Boolean(anchor);
  const active = selected.length > 0;

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleClose = () => {
    setAnchor(null);
    setMenuSearch('');
  };

  const filteredOptions = useMemo(() => {
    if (!searchable || !menuSearch.trim()) return options;
    return options.filter((o) => o.toLowerCase().includes(menuSearch.trim().toLowerCase()));
  }, [options, menuSearch, searchable]);

  return (
    <>
      <Button
        id={`filter-btn-${label.replace(/\s+/g, '-').toLowerCase()}`}
        size="small"
        endIcon={<ArrowDownIcon sx={{ fontSize: 16 }} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          textTransform: 'none',
          borderRadius: STORY_UI.radiusPill,
          fontWeight: 500,
          fontSize: '13px',
          fontFamily: STORY_UI.fontSans,
          px: 1.75,
          py: 1,
          minHeight: 0,
          backgroundColor: active ? STORY_UI.ink : STORY_UI.surface,
          color: active ? '#fff' : STORY_UI.inkSoft,
          border: `1px solid ${active ? STORY_UI.ink : STORY_UI.line}`,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: active ? STORY_UI.ink : STORY_UI.surface,
            borderColor: active ? STORY_UI.ink : STORY_UI.lineStrong,
            color: active ? '#fff' : STORY_UI.ink,
          },
        }}
      >
        {active ? (
          <Badge
            badgeContent={selected.length}
            color="error"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16, right: -6, top: -2 } }}
          >
            {label}
          </Badge>
        ) : (
          label
        )}
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 240,
            borderRadius: 2,
            boxShadow: STORY_UI.shadowPop,
            mt: 0.5,
            maxHeight: 380,
            fontFamily: STORY_UI.fontSans,
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        {searchable && (
          <Box
            sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${STORY_UI.line}`, backgroundColor: '#fafafa' }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={`Search ${label}…`}
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: STORY_UI.inkFaint }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8rem',
                  borderRadius: '8px',
                  fontFamily: STORY_UI.fontSans,
                },
              }}
            />
          </Box>
        )}
        <Box sx={{ overflowY: 'auto', maxHeight: 280 }}>
          {filteredOptions.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {menuSearch ? 'No matches' : 'No options'}
              </Typography>
            </MenuItem>
          ) : (
            filteredOptions.map((option) => {
              const checked = selected.includes(option);
              return (
                <MenuItem
                  key={option}
                  onClick={() => toggle(option)}
                  dense
                  sx={{
                    gap: 1,
                    fontSize: '0.83rem',
                    fontFamily: STORY_UI.fontSans,
                    backgroundColor: checked ? 'rgba(0,100,255,0.06)' : 'transparent',
                  }}
                >
                  {checked ? (
                    <CheckBoxIcon sx={{ color: STORY_UI.accent, fontSize: 18 }} />
                  ) : (
                    <CheckBoxBlankIcon sx={{ color: STORY_UI.inkFaint, fontSize: 18 }} />
                  )}
                  <Typography variant="inherit" noWrap>
                    {option}
                  </Typography>
                </MenuItem>
              );
            })
          )}
        </Box>
        {selected.length > 0 && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                onChange([]);
                handleClose();
              }}
              dense
              sx={{ color: '#ef4444', fontSize: '0.8rem', justifyContent: 'center' }}
            >
              Clear
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

// ─────────────────────────────────────────────────
// Sort
// ─────────────────────────────────────────────────
type SortKey = 'default' | 'updated_desc' | 'title_asc' | 'account_asc' | 'id_desc';

// The list opens on the most recently updated stories.
const DEFAULT_SORT: SortKey = 'updated_desc';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'updated_desc', label: 'Recently updated' },
  // Backend order: relevance-ranked while a search is active, title A–Z otherwise.
  { key: 'default', label: 'Relevance' },
  { key: 'title_asc', label: 'Title (A–Z)' },
  { key: 'account_asc', label: 'Account (A–Z)' },
  { key: 'id_desc', label: 'Story ID (newest first)' },
];

const storyIdNumber = (id: string): number => {
  const m = /^SS-(\d+)$/i.exec((id || '').trim());
  return m ? parseInt(m[1], 10) : -1;
};

// Blank/missing values sort last regardless of direction, so empty cards don't head the list.
const byText = (a?: string | null, b?: string | null): number => {
  const x = (a || '').trim();
  const y = (b || '').trim();
  if (!x && !y) return 0;
  if (!x) return 1;
  if (!y) return -1;
  return x.localeCompare(y, undefined, { sensitivity: 'base' });
};

const byTimeDesc = (a?: string | null, b?: string | null): number => {
  const x = a ? Date.parse(a) : NaN;
  const y = b ? Date.parse(b) : NaN;
  const xOk = !Number.isNaN(x);
  const yOk = !Number.isNaN(y);
  if (!xOk && !yOk) return 0;
  if (!xOk) return 1;
  if (!yOk) return -1;
  return y - x;
};

const SortDropdown: React.FC<{ value: SortKey; onChange: (v: SortKey) => void }> = ({
  value,
  onChange,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  // Highlighted only when the user has moved off the default, but the label always names the
  // active sort so the current ordering is never a mystery.
  const active = value !== DEFAULT_SORT;
  const current = SORT_OPTIONS.find((o) => o.key === value) ?? SORT_OPTIONS[0];

  return (
    <>
      <Button
        id="sort-by-btn"
        size="small"
        startIcon={<SortIcon sx={{ fontSize: 16 }} />}
        endIcon={<ArrowDownIcon sx={{ fontSize: 16 }} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          textTransform: 'none',
          borderRadius: STORY_UI.radiusPill,
          fontWeight: 500,
          fontSize: '13px',
          fontFamily: STORY_UI.fontSans,
          px: 1.75,
          py: 1,
          minHeight: 0,
          backgroundColor: active ? STORY_UI.ink : STORY_UI.surface,
          color: active ? '#fff' : STORY_UI.inkSoft,
          border: `1px solid ${active ? STORY_UI.ink : STORY_UI.line}`,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: active ? STORY_UI.ink : STORY_UI.surface,
            borderColor: active ? STORY_UI.ink : STORY_UI.lineStrong,
            color: active ? '#fff' : STORY_UI.ink,
          },
        }}
      >
        {current.label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: 2,
            boxShadow: STORY_UI.shadowPop,
            mt: 0.5,
            fontFamily: STORY_UI.fontSans,
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        {SORT_OPTIONS.map((option) => {
          const checked = option.key === value;
          return (
            <MenuItem
              key={option.key}
              onClick={() => {
                onChange(option.key);
                setAnchor(null);
              }}
              dense
              sx={{
                gap: 1,
                fontSize: '0.83rem',
                fontFamily: STORY_UI.fontSans,
                backgroundColor: checked ? 'rgba(0,100,255,0.06)' : 'transparent',
              }}
            >
              <CheckIcon
                sx={{ fontSize: 16, color: checked ? STORY_UI.accent : 'transparent' }}
              />
              <Typography variant="inherit" noWrap>
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

// Catalog ids are stored zero-padded to three digits (SS-007), so a hand-typed or
// externally-supplied SS-7 still resolves to the right story.
const normalizeStoryId = (raw: string): string => {
  const s = (raw || '').trim().toUpperCase();
  const m = /^SS-(\d+)$/.exec(s);
  return m ? `SS-${m[1].padStart(3, '0')}` : s;
};

// ─────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────
const SolutionStoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { storyId: deepLinkId } = useParams<{ storyId: string }>();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<SolutionStoryFilters>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailStory, setDetailStory] = useState<SolutionStory | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [generatingSlides, setGeneratingSlides] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [slidesUrl, setSlidesUrl] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>(DEFAULT_SORT);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  // Which deep-link id we've already acted on, so closing the modal doesn't reopen it.
  const handledDeepLinkRef = useRef<string | null>(null);

  const { data: filterOptions } = useQuery({
    queryKey: ['solution-stories-filters'],
    queryFn: () => solutionStoriesApi.getFilterOptions(),
    staleTime: 10 * 60 * 1000,
  });

  const { data: optionsData } = useQuery({
    queryKey: SOLUTION_STORY_OPTIONS_QUERY_KEY,
    queryFn: () => solutionStoriesApi.getOptions(),
    staleTime: 5 * 60 * 1000,
  });
  const iconMappings = optionsData?.icon_mappings;

  const { data: adminAccess } = useQuery({
    queryKey: ['solution-stories-admin-access'],
    queryFn: () => solutionStoriesApi.getAdminAccess(),
    staleTime: 5 * 60 * 1000,
  });
  const canAccessAdmin = !!adminAccess?.allowed;

  const { data: pendingAdmin } = useQuery({
    queryKey: ['solution-stories-admin-pending'],
    queryFn: () => solutionStoriesApi.listAdminPending(),
    staleTime: 60 * 1000,
    enabled: canAccessAdmin,
  });
  const pendingReviewCount = pendingAdmin?.total_count ?? 0;

  const industryLabels = iconMappings?.industry_labels;

  const industryFilterOptions = useMemo(
    () => dedupeIndustryFilterOptions(filterOptions?.industries ?? [], industryLabels),
    [filterOptions?.industries, industryLabels]
  );

  const industrySelected = useMemo(() => {
    const selected = filters.industry ?? [];
    if (!selected.length) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of selected) {
      const expanded = expandIndustryToCanonical(v, industryLabels);
      const key = expanded.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(expanded);
    }
    return out;
  }, [filters.industry, industryLabels]);

  const activeFilters: SolutionStoryFilters = useMemo(
    () => ({
      ...filters,
      ...(filters.industry?.length
        ? { industry: industryFilterValuesForApi(filters.industry, industryLabels) }
        : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [filters, search, industryLabels]
  );

  const { data: storiesData, isLoading, error } = useQuery({
    queryKey: ['solution-stories', activeFilters],
    queryFn: () => solutionStoriesApi.list(activeFilters),
    staleTime: 3 * 60 * 1000,
  });

  // Sorting is applied here rather than at render, so selection, "Select all", the deep link
  // and the modal's prev/next all follow the order the user actually sees.
  // 'default' keeps the backend's order — which is relevance-ranked when a search is active.
  const stories: SolutionStory[] = useMemo(() => {
    const base = storiesData?.stories ?? [];
    if (sortBy === 'default') return base;
    const copy = [...base];
    switch (sortBy) {
      case 'updated_desc':
        return copy.sort((a, b) => byTimeDesc(a.updated_time, b.updated_time));
      case 'title_asc':
        return copy.sort((a, b) => byText(a.solution_provided_title, b.solution_provided_title));
      case 'account_asc':
        return copy.sort((a, b) => byText(a.account_name, b.account_name));
      case 'id_desc':
        return copy.sort((a, b) => storyIdNumber(b.solution_story_id) - storyIdNumber(a.solution_story_id));
      default:
        return base;
    }
  }, [storiesData, sortBy]);

  const hasAnyFilter = useMemo(() => {
    return (
      search.trim().length > 0 ||
      Object.values(filters).some((v) => Array.isArray(v) && v.length > 0)
    );
  }, [search, filters]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).reduce((n, v) => n + (Array.isArray(v) ? (v.length > 0 ? 1 : 0) : 0), 0),
    [filters]
  );

  const handleClearAll = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setFilters({});
    setSelectedIds(new Set());
    setSlidesUrl(null);
    setGenerateError(null);
  }, []);

  const applySearch = useCallback(() => {
    setSearch(searchInput.trim());
  }, [searchInput]);

  const toggleSelect = useCallback((id: string) => {
    setSlidesUrl(null);
    setGenerateError(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const openDetail = useCallback(
    (id: string) => {
      const story = stories.find((s) => s.solution_story_id === id);
      if (!story) return;
      setDetailStory(story);
      setDetailOpen(true);
    },
    [stories]
  );

  // Searching switches to relevance, because the backend ranks hits (exact matches pinned,
  // then by similarity) and a date sort would throw that ranking away. Clearing the search
  // goes back to recently-updated. Only fires when the search itself changes, so a sort the
  // user picks by hand afterwards stays put.
  useEffect(() => {
    setSortBy(search.trim() ? 'default' : DEFAULT_SORT);
  }, [search]);

  // Deep link (/solution-story/SS-489): open that story's modal once the list has loaded.
  // Acts only once per id — after that the user can close the modal and browse normally.
  useEffect(() => {
    if (!deepLinkId) {
      handledDeepLinkRef.current = null;
      setDeepLinkError(null);
      return;
    }
    if (isLoading || handledDeepLinkRef.current === deepLinkId) return;

    handledDeepLinkRef.current = deepLinkId;
    const wanted = normalizeStoryId(deepLinkId);
    const story = stories.find((s) => normalizeStoryId(s.solution_story_id) === wanted);
    if (story) {
      setDetailStory(story);
      setDetailOpen(true);
      setDeepLinkError(null);
    } else {
      setDeepLinkError(
        `Solution story ${wanted} was not found. It may not exist yet, or may still be pending review.`
      );
    }
  }, [deepLinkId, stories, isLoading]);

  // Closing a deep-linked modal drops the id from the URL so the page behaves normally after.
  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailStory(null);
    if (deepLinkId) navigate('/solution-story', { replace: true });
  }, [deepLinkId, navigate]);

  const handleSelectAll = () => {
    setSlidesUrl(null);
    setGenerateError(null);
    if (selectedIds.size === stories.length && stories.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(stories.map((s) => s.solution_story_id)));
    }
  };

  const allSelected = stories.length > 0 && selectedIds.size === stories.length;
  const detailIndex = detailStory
    ? stories.findIndex((s) => s.solution_story_id === detailStory.solution_story_id)
    : -1;

  const handleGenerateCombinedSlides = async () => {
    if (selectedIds.size === 0 || generatingSlides || slidesUrl) return;
    setGeneratingSlides(true);
    setGenerateError(null);
    setSlidesUrl(null);
    try {
      const ids = Array.from(selectedIds);
      const res = await solutionStoriesApi.generateSlides(ids);
      if (res.presentation_url) {
        setSlidesUrl(res.presentation_url);
      } else {
        setGenerateError('Slides were created but no open link was returned');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to generate Google Slides';
      setGenerateError(typeof detail === 'string' ? detail : 'Failed to generate Google Slides');
    } finally {
      setGeneratingSlides(false);
    }
  };

  return (
    <Box
      sx={{
        mx: { xs: -2, sm: -3, md: -4 },
        mt: { xs: -1, sm: -1.5 },
        mb: -4,
        fontFamily: STORY_UI.fontSans,
        color: STORY_UI.ink,
        backgroundColor: STORY_UI.bg,
        minHeight: '100%',
        pb: '120px',
      }}
    >
      {/* ── Hero ── */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 4 },
          // Extra top padding on mobile clears the fixed hamburger button (DashboardLayout,
          // top:12/left:12, ~48px tall) so it sits above the title instead of overlapping it.
          pt: { xs: 8, sm: 3 },
          pb: 1,
          textAlign: 'center',
          backgroundColor: STORY_UI.heroBg,
          borderBottom: `1px solid ${STORY_UI.line}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 },
            position: 'relative',
            mb: { xs: '6px', sm: '10px' },
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: STORY_UI.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '20px', sm: '36px' },
              letterSpacing: '-0.02em',
              color: STORY_UI.ink,
              m: 0,
            }}
          >
            Solution Stories
          </Typography>
          {canAccessAdmin && (
          <Badge
            badgeContent={pendingReviewCount}
            color="error"
            overlap="circular"
            invisible={pendingReviewCount === 0}
            sx={{
              // Mobile: fixed top-right, mirroring the hamburger button fixed top-left
              // (DashboardLayout), instead of squeezing into the title row.
              position: { xs: 'fixed', sm: 'absolute' },
              top: { xs: 12, sm: 'auto' },
              right: { xs: 12, sm: 16 },
              zIndex: { xs: 1301, sm: 'auto' },
              '& .MuiBadge-badge': {
                fontFamily: STORY_UI.fontSans,
                fontWeight: 700,
                fontSize: '11px',
                minWidth: 20,
                height: 20,
                borderRadius: '10px',
                top: 4,
                right: 4,
                border: '2px solid #fff',
              },
            }}
          >
            <Button
              id="solution-stories-admin-btn"
              startIcon={<AdminIcon sx={{ fontSize: { xs: 20, sm: 18 }, color: '#fff' }} />}
              onClick={() => navigate('/solution-stories/admin')}
              sx={{
                textTransform: 'none',
                // Mobile: circular icon-only button matching the hamburger's look.
                borderRadius: { xs: '50%', sm: STORY_UI.radiusSm },
                minWidth: { xs: 40, sm: 'auto' },
                width: { xs: 40, sm: 'auto' },
                height: { xs: 40, sm: 'auto' },
                fontWeight: 600,
                fontSize: '13px',
                fontFamily: STORY_UI.fontSans,
                px: { xs: 0, sm: 2.5 },
                py: { xs: 0, sm: 1 },
                backgroundColor: STORY_UI.accent,
                color: '#fff',
                border: 'none',
                boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
                '&:hover': {
                  backgroundColor: STORY_UI.accentDeep,
                  color: '#fff',
                },
                '& .MuiButton-startIcon': { color: '#fff', mx: { xs: 0, sm: undefined } },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Admin
              </Box>
            </Button>
          </Badge>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: { xs: '12.5px', sm: '15.5px' },
            color: STORY_UI.inkSoft,
            maxWidth: 700,
            mx: 'auto',
            mb: { xs: '10px', sm: '15px' },
            lineHeight: 1.4,
          }}
        >
          Search and explore customer success stories across industries, technologies and regions.
        </Typography>

        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              backgroundColor: STORY_UI.surface,
              border: `1.5px solid ${STORY_UI.lineStrong}`,
              borderRadius: STORY_UI.radiusPill,
              pl: { xs: '14px', sm: '22px' },
              pr: { xs: 0.5, sm: 1 },
              py: { xs: 0.5, sm: 1 },
              boxShadow: STORY_UI.shadowCard,
              transition: 'border-color .15s ease, box-shadow .15s ease',
              '&:focus-within': {
                borderColor: STORY_UI.accent,
                boxShadow: `0 0 0 4px ${STORY_UI.accentTint}`,
              },
            }}
          >
            <SearchIcon sx={{ fontSize: { xs: 15, sm: 18 }, color: STORY_UI.inkFaint, flexShrink: 0 }} />
            <Box
              component="input"
              id="solution-stories-search"
              value={searchInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') applySearch();
              }}
              placeholder="Search by client, technology, business problem, outcome, competitor, industry, region or ask in natural language..."
              sx={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: { xs: '12.5px', sm: '14.5px' },
                fontFamily: STORY_UI.fontSans,
                background: 'transparent',
                color: STORY_UI.ink,
                py: { xs: '7px', sm: '10px' },
                minWidth: 0,
                '&::placeholder': { color: STORY_UI.inkFaint },
              }}
            />
            <Button
              id="solution-stories-search-btn"
              onClick={applySearch}
              sx={{
                backgroundColor: STORY_UI.accent,
                color: '#fff',
                borderRadius: STORY_UI.radiusPill,
                px: { xs: 1.75, sm: 3 },
                py: { xs: 0.75, sm: 1.5 },
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 600,
                textTransform: 'none',
                flexShrink: 0,
                minWidth: 0,
                '&:hover': { backgroundColor: STORY_UI.accentDeep },
              }}
            >
              Search
            </Button>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexWrap: 'wrap',
              gap: { xs: 0.75, sm: 1 },
              justifyContent: 'center',
              mt: { xs: 1.5, sm: 2.5 },
              maxWidth: 680,
              mx: 'auto',
            }}
          >
            {EXAMPLE_QUERIES.map((q) => (
              <Box
                key={q}
                component="button"
                onClick={() => {
                  setSearchInput(q);
                  //setSearch(q);
                }}
                sx={{
                  backgroundColor: STORY_UI.surface,
                  border: `1px solid ${STORY_UI.line}`,
                  color: STORY_UI.inkSoft,
                  fontSize: { xs: '11px', sm: '12.5px' },
                  px: { xs: '10px', sm: '13px' },
                  py: { xs: '5px', sm: '7px' },
                  borderRadius: STORY_UI.radiusPill,
                  fontFamily: STORY_UI.fontSans,
                  cursor: 'pointer',
                  transition: 'all .15s ease',
                  '&:hover': {
                    borderColor: STORY_UI.accent,
                    color: STORY_UI.accentDeep,
                    backgroundColor: STORY_UI.accentTint,
                  },
                }}
              >
                {q}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Shell ── */}
      <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 3, sm: 4 }, pt: 0, pb: 4 }}>
        {/* Filters — desktop: inline row */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            pt: 4,
            pb: 1,
            borderBottom: `1px solid ${STORY_UI.line}`,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: STORY_UI.inkFaint,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              mr: 0.75,
            }}
          >
            Filter
          </Typography>
          <FilterDropdown
            label="Region"
            options={filterOptions?.regions ?? []}
            selected={filters.region ?? []}
            onChange={(v) => setFilters((prev) => ({ ...prev, region: v }))}
          />
          <FilterDropdown
            label="Industry"
            options={industryFilterOptions}
            selected={industrySelected}
            onChange={(v) =>
              setFilters((prev) => ({
                ...prev,
                industry: v.map((x) => expandIndustryToCanonical(x, industryLabels)),
              }))
            }
          />
          <FilterDropdown
            label="Platform"
            options={filterOptions?.platforms ?? []}
            selected={filters.platform ?? []}
            onChange={(v) => setFilters((prev) => ({ ...prev, platform: v }))}
          />
          <FilterDropdown
            label="Technology"
            options={filterOptions?.tech_stacks ?? []}
            selected={filters.tech_stack ?? []}
            onChange={(v) => setFilters((prev) => ({ ...prev, tech_stack: v }))}
            searchable
          />
          <FilterDropdown
            label="Referenceable"
            options={filterOptions?.referencables ?? []}
            selected={filters.referencable ?? []}
            onChange={(v) => setFilters((prev) => ({ ...prev, referencable: v }))}
          />
          <SortDropdown value={sortBy} onChange={setSortBy} />
          {hasAnyFilter && (
            <Button
              id="clear-all-filters-btn"
              size="small"
              onClick={handleClearAll}
              startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
              sx={{
                ml: 0.5,
                color: '#ef4444',
                fontWeight: 500,
                fontSize: '12.5px',
                textTransform: 'none',
              }}
            >
              Clear all
            </Button>
          )}
        </Box>

        {/* Filters — mobile: compact "Filters" toggle beside Select all, dropdowns collapse below */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, pt: 3, pb: 1, borderBottom: `1px solid ${STORY_UI.line}`, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Badge
              color="primary"
              badgeContent={activeFilterCount}
              invisible={activeFilterCount === 0}
              sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: 16, minWidth: 16 } }}
            >
              <Button
                id="mobile-filters-toggle-btn"
                size="small"
                onClick={() => setMobileFiltersOpen((v) => !v)}
                startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
                sx={{
                  color: STORY_UI.ink,
                  fontWeight: 600,
                  fontSize: '12.5px',
                  textTransform: 'none',
                  border: `1px solid ${STORY_UI.line}`,
                  borderRadius: STORY_UI.radiusPill,
                  px: 1.5,
                }}
              >
                Filters
              </Button>
            </Badge>
            {!isLoading && !error && stories.length > 0 && (
              <Box
                component="button"
                id="select-all-stories-btn-mobile"
                onClick={handleSelectAll}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: STORY_UI.accent,
                  fontSize: '13px',
                  fontWeight: 600,
                  p: 0,
                  cursor: 'pointer',
                  fontFamily: STORY_UI.fontSans,
                  '&:hover': { color: STORY_UI.accentDeep, textDecoration: 'underline' },
                }}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </Box>
            )}
          </Box>
          {mobileFiltersOpen && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.25, mt: 1.5 }}>
              <FilterDropdown
                label="Region"
                options={filterOptions?.regions ?? []}
                selected={filters.region ?? []}
                onChange={(v) => setFilters((prev) => ({ ...prev, region: v }))}
              />
              <FilterDropdown
                label="Industry"
                options={industryFilterOptions}
                selected={industrySelected}
                onChange={(v) =>
                  setFilters((prev) => ({
                    ...prev,
                    industry: v.map((x) => expandIndustryToCanonical(x, industryLabels)),
                  }))
                }
              />
              <FilterDropdown
                label="Platform"
                options={filterOptions?.platforms ?? []}
                selected={filters.platform ?? []}
                onChange={(v) => setFilters((prev) => ({ ...prev, platform: v }))}
              />
              <FilterDropdown
                label="Technology"
                options={filterOptions?.tech_stacks ?? []}
                selected={filters.tech_stack ?? []}
                onChange={(v) => setFilters((prev) => ({ ...prev, tech_stack: v }))}
                searchable
              />
              <FilterDropdown
                label="Referencable"
                options={filterOptions?.referencables ?? []}
                selected={filters.referencable ?? []}
                onChange={(v) => setFilters((prev) => ({ ...prev, referencable: v }))}
              />
              <SortDropdown value={sortBy} onChange={setSortBy} />
              {hasAnyFilter && (
                <Button
                  id="clear-all-filters-btn-mobile"
                  size="small"
                  onClick={handleClearAll}
                  startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    color: '#ef4444',
                    fontWeight: 500,
                    fontSize: '12.5px',
                    textTransform: 'none',
                  }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Results meta */}
        {!isLoading && !error && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px', mb: 2 }}>
            <Typography sx={{ fontSize: '13px', color: STORY_UI.inkFaint }}>
              <Box component="b" sx={{ color: STORY_UI.ink, fontWeight: 600 }}>
                {stories.length}
              </Box>{' '}
              {stories.length === 1 ? 'story' : 'stories'} matched
            </Typography>
            {stories.length > 0 && (
              <Box
                component="button"
                id="select-all-stories-btn"
                onClick={handleSelectAll}
                sx={{
                  display: { xs: 'none', sm: 'inline' },
                  background: 'none',
                  border: 'none',
                  color: STORY_UI.accent,
                  fontSize: '13px',
                  fontWeight: 600,
                  p: 0,
                  cursor: 'pointer',
                  fontFamily: STORY_UI.fontSans,
                  '&:hover': { color: STORY_UI.accentDeep, textDecoration: 'underline' },
                }}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </Box>
            )}
            <Typography sx={{ fontSize: '12.5px', color: STORY_UI.inkFaint, fontStyle: 'italic' }}>
              Select solution stories to generate Google Slides.
            </Typography>
          </Box>
        )}

        {deepLinkError && (
          <Alert
            severity="warning"
            sx={{ borderRadius: 2, mb: 2 }}
            onClose={() => setDeepLinkError(null)}
          >
            {deepLinkError}
          </Alert>
        )}

        {/* List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={36} sx={{ color: STORY_UI.accent }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Failed to load solution stories. Please try again.
          </Alert>
        ) : stories.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 12,
              gap: 2,
            }}
          >
            <StoriesIcon sx={{ fontSize: 56, color: STORY_UI.line }} />
            <Typography sx={{ color: STORY_UI.inkFaint, fontWeight: 600, fontSize: '1.1rem' }}>
              No stories found
            </Typography>
            <Typography sx={{ color: STORY_UI.inkFaint, fontSize: '13.5px' }}>
              {hasAnyFilter
                ? 'Try adjusting your filters or search term.'
                : 'No solution stories have been added yet.'}
            </Typography>
            {hasAnyFilter && (
              <Button
                variant="outlined"
                onClick={handleClearAll}
                size="small"
                sx={{ mt: 1, textTransform: 'none', borderRadius: 2 }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {stories.map((story) => (
              <StoryCard
                key={story.solution_story_id}
                story={story}
                selected={selectedIds.has(story.solution_story_id)}
                onToggleSelect={toggleSelect}
                onCardClick={openDetail}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Bulk bar ── */}
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform:
            selectedIds.size > 0
              ? 'translate(-50%, 0)'
              : 'translate(-50%, calc(100% + 48px))',
          opacity: selectedIds.size > 0 ? 1 : 0,
          visibility: selectedIds.size > 0 ? 'visible' : 'hidden',
          backgroundColor: STORY_UI.ink,
          color: '#fff',
          borderRadius: STORY_UI.radiusPill,
          pl: '22px',
          pr: '10px',
          py: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          boxShadow: STORY_UI.shadowPop,
          zIndex: 80,
          // Hide: slide down + fade, then drop visibility so nothing peeks.
          // Show: make visible immediately, then slide/fade in.
          transition: selectedIds.size > 0
            ? 'transform .25s ease, opacity .2s ease, visibility 0s'
            : 'transform .25s ease, opacity .2s ease, visibility 0s linear .25s',
          pointerEvents: selectedIds.size > 0 ? 'auto' : 'none',
        }}
      >
        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {selectedIds.size} selected
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {slidesUrl ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'rgba(255,255,255,0.12)',
                borderRadius: STORY_UI.radiusPill,
                pl: 1.75,
                pr: 0.75,
                py: 0.5,
              }}
            >
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Slides have been created
              </Typography>
              <Button
                id="bulk-open-slides-btn"
                component="a"
                href={slidesUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: STORY_UI.accent,
                  color: '#fff',
                  borderRadius: STORY_UI.radiusPill,
                  px: 1.75,
                  py: 0.75,
                  minWidth: 0,
                  fontSize: '12.5px',
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  '&:hover': { backgroundColor: STORY_UI.accentDeep },
                }}
              >
                Open now
              </Button>
            </Box>
          ) : (
            <Button
              id="bulk-generate-slides-btn"
              disabled={generatingSlides}
              onClick={handleGenerateCombinedSlides}
              startIcon={
                generatingSlides ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : undefined
              }
              sx={{
                backgroundColor: STORY_UI.accent,
                color: '#fff',
                borderRadius: STORY_UI.radiusPill,
                px: 2,
                py: 1,
                fontSize: '12.5px',
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: STORY_UI.accentDeep },
                '&.Mui-disabled': { backgroundColor: STORY_UI.accent, color: '#fff', opacity: 0.55 },
              }}
            >
              {generatingSlides ? 'Generating…' : 'Generate Combined Google Slides'}
            </Button>
          )}
        </Box>
        <Button
          id="bulk-clear-btn"
          onClick={() => {
            setSelectedIds(new Set());
            setSlidesUrl(null);
            setGenerateError(null);
          }}
          sx={{
            background: 'none',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '12px',
            textTransform: 'none',
            minWidth: 0,
            px: 1,
            '&:hover': { background: 'none', color: '#fff' },
          }}
        >
          Clear
        </Button>
      </Box>

      {generateError && (
        <Alert
          severity="error"
          onClose={() => setGenerateError(null)}
          sx={{
            position: 'fixed',
            bottom: selectedIds.size > 0 ? 88 : 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 90,
            maxWidth: 520,
            boxShadow: STORY_UI.shadowPop,
          }}
        >
          {generateError}
        </Alert>
      )}

      <SolutionStoryDetailModal
        open={detailOpen}
        onClose={closeDetail}
        story={detailStory}
        stories={stories}
        currentIndex={detailIndex}
        onNavigate={(idx) => {
          if (idx >= 0 && idx < stories.length) {
            setDetailStory(stories[idx]);
          }
        }}
      />
    </Box>
  );
};

export default SolutionStoriesPage;
